import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Navigate, Link } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { Loader2, LogOut, ScanLine, UserCheck, AlertOctagon, Printer, Presentation, Search, Users, Clock, DoorOpen } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { PhotoThumb } from "../components/ImageLightbox";
import { TimerPanel } from "../components/TimerPanel";
import { IncidentModal } from "../components/IncidentModal";
import { ResultBanner } from "./CheckinPage";
import { ConnectivityBadge, pushQueue } from "../lib/online";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { api, apiError, fileUrl } from "../lib/api";

const SALON_ROLES = ["admin", "welcome_coordinator", "room_manager"];

const STATE_LABELS = {
  draft: "Borrador", scheduled: "Programado", starting_soon: "Próximo a iniciar",
  in_progress: "En curso", completed: "Finalizado", closed: "Cerrado", cancelled: "Cancelado",
};
const STATE_CLS = {
  draft: "bg-hz-gray/30 text-hz-ink/70", scheduled: "bg-hz-blue/10 text-hz-blue",
  starting_soon: "bg-hz-warning/10 text-hz-warning", in_progress: "bg-hz-cyan/10 text-hz-blue",
  completed: "bg-hz-success/10 text-hz-success", closed: "bg-hz-gray/40 text-hz-ink",
  cancelled: "bg-hz-ink/10 text-hz-ink/70",
};

const useSalonUser = () => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  useEffect(() => {
    const token = localStorage.getItem("hz_admin_token");
    if (!token) { setChecking(false); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem("hz_admin_token"))
      .finally(() => setChecking(false));
  }, []);
  return { user, checking };
};

const SalonIndex = () => {
  const navigate = useNavigate();
  const { user, checking } = useSalonUser();
  const [workshops, setWorkshops] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/salon/workshops")
      .then((r) => setWorkshops(r.data.workshops || []))
      .catch((e) => {
        if (e?.response?.status === 403) setForbidden(true);
        else toast.error(apiError(e));
      });
  }, [user]);

  if (checking) return <div className="flex min-h-screen items-center justify-center bg-hz-bg text-hz-navy"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!user) return <Navigate to="/login?redirect=/salon" replace />;
  if (forbidden || !SALON_ROLES.includes(user.role)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-hz-bg px-4 text-center" data-testid="salon-no-perms">
        <AlertOctagon className="h-12 w-12 text-hz-error" />
        <p className="mt-4 font-bold text-hz-navy">Tu rol no tiene acceso a la vista de salón.</p>
        <Link to="/login" className="mt-4 text-sm font-bold text-hz-cyan underline">Cambiar de usuario</Link>
      </div>
    );
  }
  if (!workshops) return <div className="flex min-h-screen items-center justify-center bg-hz-bg text-hz-navy"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (user.role === "room_manager" && workshops.length === 1) return <Navigate to={`/salon/${workshops[0].id}`} replace />;

  return (
    <div className="min-h-screen bg-hz-bg" data-testid="salon-index">
      <header className="border-b border-hz-gray/50 bg-white">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <Logo variant="title" className="h-8" />
            <span className="rounded-full bg-hz-cyan/10 px-3 py-1 text-xs font-bold text-hz-blue">Salones</span>
          </div>
          <ConnectivityBadge />
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 py-8">
        <h1 className="text-2xl font-bold tracking-tight text-hz-navy">
          {user.role === "room_manager" ? "Mis talleres asignados" : "Vista operativa de talleres"}
        </h1>
        {workshops.length === 0 ? (
          <p className="mt-6 rounded-xl border border-hz-gray/40 bg-white p-6 text-sm text-hz-ink/60" data-testid="salon-empty">
            No tienes talleres asignados. Contacta al equipo de administración.
          </p>
        ) : (
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {workshops.map((w) => (
              <button key={w.id} onClick={() => navigate(`/salon/${w.id}`)} data-testid={`salon-card-${w.id}`}
                className="rounded-2xl border border-hz-gray/40 bg-white p-5 text-left shadow-sm transition-shadow hover:shadow-md">
                <div className="flex items-center justify-between">
                  <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATE_CLS[w.state]}`}>{STATE_LABELS[w.state]}</span>
                  {w.occupancy === "full" && <span className="rounded-full bg-hz-error/10 px-3 py-1 text-xs font-bold text-hz-error">Lleno</span>}
                  {w.occupancy === "almost_full" && <span className="rounded-full bg-hz-warning/10 px-3 py-1 text-xs font-bold text-hz-warning">Casi lleno</span>}
                </div>
                <p className="mt-3 text-lg font-bold text-hz-ink">{w.name}</p>
                <p className="mt-1 text-xs text-hz-ink/60">
                  {w.speaker_name || "Ponente por confirmar"} · {w.room_name || "Salón por confirmar"}
                </p>
                <p className="mt-2 text-sm font-semibold text-hz-navy">
                  {w.present_count}/{w.registered_count} presentes · {w.available} lugares disponibles
                </p>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export const SalonDetail = () => {
  const { workshopId } = useParams();
  const navigate = useNavigate();
  const { user, checking } = useSalonUser();
  const [detail, setDetail] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [tab, setTab] = useState("asistencia");
  const [q, setQ] = useState("");
  const [controlOpen, setControlOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [manualRow, setManualRow] = useState(null);
  const [manualReason, setManualReason] = useState("");
  const [incidentOpen, setIncidentOpen] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const lastScanRef = useRef(0);

  const load = useCallback(() => {
    api.get(`/salon/workshops/${workshopId}`)
      .then((r) => setDetail(r.data))
      .catch((e) => {
        if (e?.response?.status === 403) setForbidden(true);
        else if (e?.response?.status !== 401) toast.error(apiError(e));
      });
  }, [workshopId]);

  useEffect(() => {
    if (!user) return;
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [user, load]);

  const validate = useCallback(async ({ token, attendee_id, manual = false, reason = null }) => {
    const payload = { token: token || null, attendee_id: attendee_id || null, checkpoint_type: "workshop", workshop_id: workshopId, manual, reason };
    try {
      const { data } = await api.post("/checkin/validate", payload);
      setResult(data);
      load();
    } catch (err) {
      if (!err?.response) {
        pushQueue(payload);
        setResult({ result: "pending", message: "Sin conexión: la operación quedó en cola y se sincronizará automáticamente.", attendee: null });
      } else {
        setResult({ result: "error", message: apiError(err, "No se pudo validar el acceso.") });
      }
    }
  }, [workshopId, load]);

  useEffect(() => {
    if (!controlOpen || !user) return;
    const scanner = new Html5Qrcode("salon-qr-reader");
    let active = true;
    let started = false;
    setCameraError(null);
    scanner.start(
      { facingMode: "environment" },
      { fps: 8, qrbox: { width: 220, height: 220 } },
      (text) => {
        const now = Date.now();
        if (!active || now - lastScanRef.current < 3000) return;
        lastScanRef.current = now;
        validate({ token: text });
      },
      () => {}
    ).then(() => { started = true; }).catch(() => { if (active) setCameraError("No se pudo acceder a la cámara. Usa la asistencia manual."); });
    return () => {
      active = false;
      if (!started) return;
      try { scanner.stop().then(() => scanner.clear()).catch(() => {}); } catch (e) {}
    };
  }, [controlOpen, user, validate]);

  if (checking) return <div className="flex min-h-screen items-center justify-center bg-hz-bg text-hz-navy"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!user) return <Navigate to={`/login?redirect=/salon/${workshopId}`} replace />;
  if (forbidden) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-hz-bg px-4 text-center" data-testid="salon-detail-forbidden">
        <AlertOctagon className="h-12 w-12 text-hz-error" />
        <p className="mt-4 font-bold text-hz-navy">No tienes permiso para operar este taller.</p>
        <Button onClick={() => navigate("/salon")} className="mt-4 bg-hz-navy font-bold text-white" data-testid="salon-back-btn">Volver a mis talleres</Button>
      </div>
    );
  }
  if (!detail) return <div className="flex min-h-screen items-center justify-center bg-hz-bg text-hz-navy"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const w = detail.workshop;
  const m = detail.metrics;
  const filtered = detail.attendees.filter((a) =>
    !q.trim() || a.full_name.toLowerCase().includes(q.toLowerCase()) || a.folio.toLowerCase().includes(q.toLowerCase()) || (a.phone || "").includes(q.trim())
  );

  const logout = () => {
    localStorage.removeItem("hz_admin_token");
    navigate("/login");
  };

  const metrics = [
    { label: "Cupo máximo", value: w.capacity ?? "—", testId: "metric-capacity" },
    { label: "Inscritos", value: m.registered_count, testId: "metric-registered" },
    { label: "Presentes", value: m.present_count, testId: "metric-present" },
    { label: "Ausentes", value: m.absent_count, testId: "metric-absent" },
    { label: "Disponibles", value: m.available, testId: "metric-available" },
  ];

  return (
    <div className="min-h-screen bg-hz-bg" data-testid="salon-detail">
      <header className="border-b border-hz-gray/50 bg-white">
        <div className="mx-auto flex min-h-16 max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-3">
            <Logo variant="icon" className="h-9" />
            <div>
              <p className="text-base font-extrabold text-hz-navy" data-testid="salon-workshop-name">{w.name}</p>
              <p className="text-xs font-medium text-hz-ink/60">
                {w.speaker_name || "Ponente por confirmar"} · Responsable: {detail.responsible_name || "—"} · {w.room_name || "Salón —"}{w.room_location ? ` (${w.room_location})` : ""}
              </p>
              <p className="text-xs font-semibold text-hz-blue">
                {w.starts_at ? new Date(w.starts_at).toLocaleString("es-MX") : "Horario por confirmar"}{w.ends_at ? ` — ${new Date(w.ends_at).toLocaleTimeString("es-MX")}` : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATE_CLS[w.status === "published" ? (m.state === "scheduled" ? "scheduled" : m.state) : w.status] || ""}`} data-testid="salon-state">
              {STATE_LABELS[m.state] || w.status}
            </span>
            <ConnectivityBadge />
            <Button variant="outline" size="sm" onClick={logout} data-testid="salon-logout-btn" className="font-semibold text-hz-error"><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          {metrics.map((x) => (
            <div key={x.label} className="rounded-xl border border-hz-gray/40 bg-white p-4 text-center" data-testid={x.testId}>
              <p className="text-2xl font-extrabold text-hz-navy">{x.value}</p>
              <p className="text-xs font-semibold text-hz-ink/60">{x.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-4">
          <ResultBanner result={result} onClear={() => setResult(null)} />
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-4">
          <TabsList className="bg-white">
            <TabsTrigger value="asistencia" data-testid="salon-tab-asistencia" className="font-semibold"><Users className="mr-1.5 h-4 w-4" />Asistencia</TabsTrigger>
            <TabsTrigger value="temporizador" data-testid="salon-tab-timer" className="font-semibold"><Clock className="mr-1.5 h-4 w-4" />Temporizador</TabsTrigger>
          </TabsList>

          <TabsContent value="asistencia" className="mt-4 space-y-4">
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setControlOpen((v) => !v)} data-testid="salon-toggle-scanner-btn" className="bg-hz-cyan font-bold text-white hover:bg-hz-blue">
                <ScanLine className="mr-1 h-4 w-4" /> {controlOpen ? "Cerrar control de acceso" : "Abrir control de acceso"}
              </Button>
              <Button onClick={() => setIncidentOpen(true)} variant="outline" data-testid="salon-incident-btn" className="font-bold text-hz-warning">
                <AlertOctagon className="mr-1 h-4 w-4" /> Reportar incidencia
              </Button>
              <Button onClick={() => navigate(`/salon/${workshopId}/pantalla`)} variant="outline" data-testid="salon-projection-btn" className="font-bold text-hz-navy">
                <Presentation className="mr-1 h-4 w-4" /> Pantalla de proyección
              </Button>
              {["admin", "welcome_coordinator"].includes(user.role) && (
                <Button onClick={() => navigate(`/admin/print/taller/${workshopId}`)} variant="outline" data-testid="salon-print-btn" className="font-bold text-hz-navy">
                  <Printer className="mr-1 h-4 w-4" /> Imprimir lista
                </Button>
              )}
            </div>

            {controlOpen && (
              <div className="rounded-2xl border border-hz-gray/40 bg-white p-4" data-testid="salon-scanner-panel">
                {cameraError ? (
                  <p className="py-6 text-center text-sm font-semibold text-hz-warning" data-testid="salon-camera-error">{cameraError}</p>
                ) : (
                  <div id="salon-qr-reader" className="mx-auto w-full max-w-sm overflow-hidden rounded-xl" data-testid="salon-qr-reader" />
                )}
                <p className="mt-2 text-center text-xs text-hz-ink/60">Escáner limitado a este taller: {w.name}</p>
              </div>
            )}

            <form onSubmit={(e) => e.preventDefault()} className="flex items-center gap-2">
              <Search className="h-4 w-4 text-hz-ink/40" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, folio o teléfono" data-testid="salon-search-input" className="bg-white" />
            </form>

            <div className="divide-y divide-hz-gray/30 rounded-2xl border border-hz-gray/40 bg-white" data-testid="salon-attendee-list">
              {filtered.length === 0 && <p className="px-4 py-6 text-sm text-hz-ink/60" data-testid="salon-list-empty">Sin asistentes para mostrar.</p>}
              {filtered.map((a) => (
                <div key={a.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`salon-attendee-${a.folio}`}>
                  {a.photo_url && <PhotoThumb src={a.photo_url} alt={`Fotografía de ${a.full_name}`} className="h-11 w-11 rounded-lg border border-hz-gray/50 object-cover" testId={`salon-attendee-photo-${a.id || a.folio}`} />}
                  <div className="min-w-40 flex-1">
                    <p className="text-sm font-bold text-hz-ink">{a.full_name}</p>
                    <p className="text-xs font-semibold text-hz-navy">{a.folio}</p>
                    <p className="text-xs text-hz-ink/60">{a.delegation_name} · {a.sector_name}</p>
                  </div>
                  {a.checked_in ? (
                    <span className="rounded-full bg-hz-success/10 px-3 py-1 text-xs font-bold text-hz-success" data-testid={`present-${a.folio}`}>
                      Presente {a.checked_at ? `· ${new Date(a.checked_at).toLocaleTimeString("es-MX")}` : ""}
                    </span>
                  ) : a.entitlement_status === "active" ? (
                    <span className="rounded-full bg-hz-cyan/10 px-3 py-1 text-xs font-bold text-hz-blue">Autorizado · ausente</span>
                  ) : (
                    <span className="rounded-full bg-hz-warning/10 px-3 py-1 text-xs font-bold text-hz-warning">Acceso pendiente</span>
                  )}
                  {!a.checked_in && (
                    <Button size="sm" variant="outline" onClick={() => { setManualRow(a); setManualReason(""); }} data-testid={`salon-manual-${a.folio}`} className="font-bold text-hz-navy">
                      <UserCheck className="mr-1 h-4 w-4" /> Manual
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="temporizador" className="mt-4">
            <TimerPanel workshopId={workshopId} role={user.role} onActionDone={load} />
            {w.instructions && (
              <div className="mt-4 rounded-xl border border-hz-gray/40 bg-white p-4" data-testid="salon-instructions">
                <p className="text-xs font-bold uppercase tracking-widest text-hz-blue">Instrucciones para el responsable</p>
                <p className="mt-1 text-sm text-hz-ink/80">{w.instructions}</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={!!manualRow} onOpenChange={(o) => !o && setManualRow(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-bold text-hz-navy">Registro manual de asistencia</DialogTitle></DialogHeader>
          {manualRow && (
            <div className="space-y-3">
              <p className="text-sm text-hz-ink"><span className="font-bold">{manualRow.full_name}</span> · {manualRow.folio}</p>
              <p className="text-xs text-hz-ink/60">Se validará su derecho de acceso en el servidor y el motivo quedará en la bitácora.</p>
              <div>
                <Label className="text-xs font-semibold">Motivo *</Label>
                <Input value={manualReason} onChange={(e) => setManualReason(e.target.value)} data-testid="salon-manual-reason" className="mt-1" placeholder="Ej. El asistente no puede mostrar su QR" />
              </div>
              <Button
                onClick={() => { validate({ attendee_id: manualRow.id, manual: true, reason: manualReason }); setManualRow(null); }}
                disabled={manualReason.trim().length < 5}
                data-testid="salon-manual-confirm"
                className="w-full bg-hz-navy font-bold text-white"
              >
                Registrar asistencia
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <IncidentModal open={incidentOpen} onClose={() => setIncidentOpen(false)} workshopId={workshopId} />
    </div>
  );
};

export default SalonIndex;
